import styled from "styled-components";
import { Box, Paper } from "@mui/material";

export const EditorRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: #0a0a0f;
  overflow: hidden;
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;

  & ::selection {
    background: transparent;
  }
`;

export const ToolbarWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1, 2)};
  background-color: #111118;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  z-index: 10;
  min-height: 52px;
`;

export const ToolbarDivider = styled(Box)`
  width: 1px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.12);
  margin: 0 ${({ theme }) => theme.spacing(0.5)};
`;

export const ToolbarSpacer = styled(Box)`
  flex: 1;
`;

export const ClipboardChip = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  background: rgba(247, 201, 72, 0.12);
  border: 1px solid rgba(247, 201, 72, 0.45);
  color: #f7c948;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  user-select: none;
`;

export const SelectionPill = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  background: rgba(155, 70, 225, 0.18);
  border: 1px solid rgba(155, 70, 225, 0.45);
  color: #c89bf2;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  user-select: none;
`;

export const MainArea = styled(Box)`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`;

export const CanvasArea = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #0a0a0f;
  padding: ${({ theme }) => theme.spacing(2)};
  overflow: auto;
  position: relative;
  min-width: 0;
`;

export const PreviewPanel = styled(Box)`
  width: ${({ $expanded }) => ($expanded ? "auto" : "240px")};
  min-width: ${({ $expanded }) => ($expanded ? "0" : "240px")};
  background-color: ${({ $expanded }) => ($expanded ? "transparent" : "#111118")};
  border-left: ${({ $expanded }) =>
    $expanded ? "none" : "1px solid rgba(255, 255, 255, 0.08)"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $expanded }) => ($expanded ? "center" : "flex-start")};
  padding: ${({ theme }) => theme.spacing(2, 1)};
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: hidden;

  [dir="rtl"] & {
    border-left: none;
    border-right: ${({ $expanded }) =>
      $expanded ? "none" : "1px solid rgba(255, 255, 255, 0.08)"};
  }

  /* Give the video canvas more room on a phone. */
  @media (max-width: 768px) {
    width: ${({ $expanded }) => ($expanded ? "auto" : "140px")};
    min-width: ${({ $expanded }) => ($expanded ? "0" : "140px")};
  }
`;

export const PreviewLabel = styled(Box)`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
`;

export const PreviewContainer = styled(Box)`
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  border: 2px solid #9b46e1;
  box-shadow: 0 0 24px rgba(155, 70, 225, 0.25);
  background: #000;
  flex-shrink: 0;
`;

export const TimelineArea = styled(Box)`
  height: 148px;
  min-height: 148px;
  background-color: #111118;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
`;

export const TimelineScroll = styled(Box)`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  cursor: crosshair;
  background: #0c0c14;
  padding-left: 16px;

  &::-webkit-scrollbar {
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.03);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.18);
    border-radius: 3px;
    &:hover {
      background: rgba(255, 255, 255, 0.32);
    }
  }
`;

export const TimelineInner = styled(Box)`
  position: relative;
  height: 100%;
`;

export const TimeRuler = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 26px;
  pointer-events: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(0, 0, 0, 0.25);
`;

export const RulerTick = styled(Box)`
  position: absolute;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.16);
  transform: translateX(-50%);
`;

export const RulerLabel = styled(Box)`
  position: absolute;
  top: 5px;
  transform: translateX(-50%);
  font-size: 0.54rem;
  color: rgba(255, 255, 255, 0.28);
  font-family: monospace;
  white-space: nowrap;
  user-select: none;
  pointer-events: none;
`;

export const SceneTrack = styled(Box)`
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  height: 56px;
`;

export const TimelineHeader = styled(Box)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(0.5, 1.5)};
  gap: ${({ theme }) => theme.spacing(1)};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

export const ScrubBar = styled(Box)`
  flex: 1;
  position: relative;
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  cursor: pointer;
  transition: height 0.12s ease;
  margin: 0 ${({ theme }) => theme.spacing(1)};
  &:hover {
    height: 7px;
    background: rgba(255, 255, 255, 0.12);
  }
`;

export const ScrubFill = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: linear-gradient(90deg, #9b46e1 0%, #c89bf2 100%);
  border-radius: 2px;
  pointer-events: none;
`;

export const ScrubKnob = styled(Box)`
  position: absolute;
  top: 50%;
  width: 11px;
  height: 11px;
  background: #fff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 6px rgba(155, 70, 225, 0.6);
  pointer-events: none;
`;

export const SceneBlock = styled(Box)`
  position: absolute;
  top: 5px;
  height: calc(100% - 10px);
  border-radius: 5px;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: filter 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);

  &:hover {
    filter: brightness(1.2);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  }

  &.selected {
    border-color: rgba(255, 255, 255, 0.72);
    filter: brightness(1.25);
    box-shadow: 0 0 14px rgba(255, 255, 255, 0.1), 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  &.multi-selected {
    border-color: #f7c948;
    border-style: dashed;
    box-shadow: 0 0 0 1px rgba(247, 201, 72, 0.35), 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  &.multi-selected.selected {
    border-style: solid;
    border-color: #f7c948;
    box-shadow: 0 0 14px rgba(247, 201, 72, 0.4), 0 2px 8px rgba(0, 0, 0, 0.5);
  }
`;

export const SceneBlockLabel = styled(Box)`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  pointer-events: none;
  user-select: none;
  text-align: center;
  padding: 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
`;

export const SceneBlockTime = styled(Box)`
  font-size: 0.52rem;
  color: rgba(255, 255, 255, 0.5);
  font-family: monospace;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding: 0 6px;
  margin-top: 1px;
`;

export const PlayheadLine = styled(Box)`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255, 255, 255, 0.92);
  pointer-events: none;
  z-index: 10;
  transform: translateX(-50%);
  box-shadow: 0 0 8px 1px rgba(255, 255, 255, 0.22), 0 0 2px rgba(255, 255, 255, 0.7);

  /* downward triangle head sitting at the ruler / scene-track boundary */
  &::before {
    content: "";
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 7px solid rgba(255, 255, 255, 0.92);
  }
`;

export const PlayheadTimeLabel = styled(Box)`
  position: absolute;
  top: 3px;
  left: 6px;
  font-size: 0.58rem;
  font-family: monospace;
  color: rgba(255, 255, 255, 0.95);
  background: rgba(0, 0, 0, 0.72);
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
`;

export const VideoCanvasWrapper = styled(Box)`
  position: relative;
  display: inline-block;
  line-height: 0;
  user-select: none;
`;

export const VideoElement = styled("video")`
  display: block;
  pointer-events: none;
  width: auto;
  height: auto;
  /* max-width / max-height are set via inline style in VideoCanvas (zoom-aware) */
`;

export const OverlayContainer = styled(Box)`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export const DarkOverlaySide = styled(Box)`
  position: absolute;
  background: rgba(0, 0, 0, 0.6);
  pointer-events: none;
`;

export const CropFrame = styled(Box)`
  position: absolute;
  border: 2.5px solid #9b46e1;
  cursor: grab;
  box-sizing: border-box;
  pointer-events: all;

  &:active {
    cursor: grabbing;
  }

  box-shadow: 0 0 0 1px rgba(155, 70, 225, 0.3), inset 0 0 0 1px rgba(155, 70, 225, 0.15);
`;

export const CornerHandle = styled(Box)`
  position: absolute;
  width: 10px;
  height: 10px;
  background: #9b46e1;
  border-radius: 2px;
  pointer-events: none;
`;

export const LoadingOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 20;
`;

export const VideoControlsBar = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: #111118;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0 0 6px 6px;
  width: 100%;
  box-sizing: border-box;
`;

export const ProgressBar = styled(Box)`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  transition: height 0.1s;
  &:hover {
    height: 6px;
  }
`;

export const ProgressFill = styled(Box)`
  height: 100%;
  background: #9b46e1;
  border-radius: 2px;
  pointer-events: none;
`;
